import React from 'react';
import styled, { css } from 'styled-components';

import { humanizeEnum } from '../../utils';
import { Button } from './button';
import { Dot } from './dot';
import { colorScheme } from '../../theme';
import { useEventTypes } from '../../hooks';

interface Props {
  name: string;
  identifier: string;
  onClick?: (id: string) => void;
  classNames?: string;
  tooltip?: string;
  shouldBreak?: boolean;
}

export const EventTypeTag = (props: Props) => {
  const eventTypes = useEventTypes();

  const getColor = (): string => {
    const markerColorHex = eventTypes.find((et) => et.identifier === props.identifier)?.marker_color_hex;
    return markerColorHex ? `#${markerColorHex}` : 'gray';
  };

  const { identifier, onClick, classNames, shouldBreak, ...passProps } = props;
  const text = eventTypes.find((et) => et.identifier === props.identifier)?.name ?? humanizeEnum(props.identifier);
  const arr = text.split(' - ');
  return (
    <ButtonTag
      className={`btn btn-sm btn-light ${classNames}`}
      onClick={() => onClick && onClick(identifier)}
      data-test="event-type-tag"
      {...passProps}
    >
      <Dot color={getColor()} size="sm" />
      {shouldBreak ? (
        <>
          {arr[0]}
          {arr.length > 1 && <br />}
          {arr[1] && (
            <>
              <Dot color="transparent" size="sm" />
              {arr[1]}
            </>
          )}
        </>
      ) : (
        text
      )}
    </ButtonTag>
  );
};

export const ButtonTag = styled(Button)<{ allowedListed?: boolean }>`
  border-radius: 5px;
  background-color: ${colorScheme.buttons.gray.background};
  color: ${colorScheme.buttons.gray.text};
  transition:
    300ms linear background,
    border;
  border: 1px solid ${colorScheme.buttons.gray.border};

  ${({ allowedListed }) =>
    allowedListed &&
    css`
      position: relative;
      top: 0;
      overflow: hidden;

      &:after {
        content: '';
        position: absolute;
        top: -6px;
        left: 3px;
        background: ${colorScheme.approve};
        display: block;
        width: 5px;
        height: 30px;
        transform: rotate(45deg);
      }
    `};

  &:active,
  &:focus {
    box-shadow: none;
    outline: none;
    background-color: ${colorScheme.buttons.secondary.background};
    border-color: ${colorScheme.buttons.secondary.border};
  }

  &:hover {
    background-color: ${colorScheme.buttons.gray.background};
    color: ${colorScheme.buttons.gray.text};
  }

  &.selected {
    border: 1px solid ${colorScheme.input.border};
    z-index: 10;
  }

  &.text-left {
    text-align: left;
  }

  &.btn.no-cursor {
    /* need this to break bootstrap inheritance */
    cursor: default;

    &:hover {
      background: white;
      border: 1px solid ${colorScheme.buttons.gray.border};
    }
  }
`;
